import type { ReactNode } from 'react'
import type { TrainingBlockItem } from '../../types/api'
import { countExercisesInItems, sortExercisesInItem } from './blockItemHelpers'
import { BlockIndexLabel } from './BlockIndexLabel'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'

export interface TrainingBlockCardProps {
  name: string
  items?: TrainingBlockItem[] | null
  tags?: string[]
  tagEmojiMap: Record<string, string>
  tagLabelMap?: Record<string, string>
  /** Optional 1-based slot / day number shown in the header. */
  index?: number
  selected?: boolean
  onClick?: () => void
  headerTrailing?: ReactNode
  footer?: ReactNode
  banner?: ReactNode
}

/** Expanded training-block card used on the blocks list, program detail, and program edit. */
export function TrainingBlockCard({
  name,
  items,
  tags = [],
  tagEmojiMap,
  tagLabelMap = {},
  index,
  selected = false,
  onClick,
  headerTrailing,
  footer,
  banner,
}: TrainingBlockCardProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const list = items ?? []
  const exerciseCount = countExercisesInItems(list)

  return (
    <div
      className={`rounded-2xl overflow-hidden${onClick ? ' cursor-pointer transition-opacity hover:opacity-90 active:opacity-70' : ''}`}
      style={{
        backgroundColor: selected ? `${theme.buttonColor}10` : theme.cardBg,
        border: selected
          ? `1.5px solid ${theme.buttonColor}60`
          : `1px solid ${theme.cardBorder}`,
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {index != null && (
              <BlockIndexLabel index={index} className="block mb-0.5" />
            )}
            <p className="font-semibold truncate" style={{ color: theme.text.primary }}>
              {name}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums"
                style={{
                  backgroundColor: theme.inputBgSubtle,
                  color: theme.text.muted,
                }}
              >
                {t.trainingBlocks.exerciseCount_other.replace('{{count}}', String(exerciseCount))}
              </span>
              {tags.length > 0 && (
                <span className="text-sm leading-none tracking-wide">
                  {tags.map(tag => tagEmojiMap[tag] ?? '').filter(Boolean).join(' ')}
                </span>
              )}
            </div>
          </div>
          {headerTrailing}
        </div>
      </div>

      {exerciseCount > 0 ? (
        <div style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
          {[...list]
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((item, idx, arr) => {
              const names = sortExercisesInItem(item.exercises)
                .map(ex => ex.name?.trim())
                .filter(Boolean) as string[]
              if (names.length === 0) return null
              const isLast = idx === arr.length - 1
              const displayIndex = item.sortOrder != null ? item.sortOrder + 1 : idx + 1
              const note = item.note?.trim()
              return (
                <div
                  key={item.id ?? `item-${idx}`}
                  className="flex items-start gap-3 px-4 py-2.5"
                  style={{ borderBottom: isLast ? undefined : `1px solid ${theme.cardBorder}` }}
                >
                  <span
                    className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
                    style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
                  >
                    {displayIndex}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" style={{ color: theme.text.primary }}>
                      {names.join(' · ')}
                    </p>
                    {note && (
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: theme.text.muted }}>
                        {note}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
        </div>
      ) : (
        <div
          className="px-4 py-3"
          style={{ borderTop: `1px solid ${theme.cardBorder}` }}
        >
          <p className="text-sm text-center" style={{ color: theme.text.muted }}>
            {t.trainingBlocks.noExercises}
          </p>
        </div>
      )}

      {tags.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 px-4 py-2.5"
          style={{ borderTop: `1px solid ${theme.cardBorder}` }}
        >
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: `${theme.buttonColor}12`,
                border: `1px solid ${theme.buttonColor}25`,
                color: theme.accent.text,
              }}
            >
              <span className="leading-none">{tagEmojiMap[tag] ?? ''}</span>
              <span>{t.trainingTags[tag as keyof typeof t.trainingTags] ?? tagLabelMap[tag] ?? tag}</span>
            </span>
          ))}
        </div>
      )}

      {banner}

      {footer && (
        <div
          className="flex items-center justify-between px-3 py-2"
          style={{ borderTop: `1px solid ${theme.cardBorder}` }}
          onClick={e => e.stopPropagation()}
        >
          {footer}
        </div>
      )}
    </div>
  )
}
